import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, CheckCircle2 } from "lucide-react";
import Reveal from "./Reveal";
import { keySectors, otherSectors, keySectorsIntro } from "../mock";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const NarrativeBlock = ({ label, children }) => (
  <div>
    <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E4545F]">
      {label}
    </h4>
    <p className="mt-2 text-sm leading-relaxed text-slate-600">{children}</p>
  </div>
);

const KeySectors = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="key-sectors" className="scroll-mt-20 bg-white py-14 md:py-20" data-testid="key-sectors-section">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <Reveal className="mb-12 max-w-3xl">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E4545F]">
            {keySectorsIntro.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-[#0F2A4A] md:text-4xl">
            {keySectorsIntro.heading}
          </h2>
          <p className="mt-4 text-lg font-light leading-relaxed text-slate-600">
            {keySectorsIntro.intro}
          </p>
        </Reveal>

        {/* Six core sectors — large cinematic cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {keySectors.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 100}>
              <button
                type="button"
                onClick={() => setActive(s)}
                data-testid={`sector-card-${s.id}`}
                className="group relative block h-full w-full overflow-hidden rounded-2xl text-left shadow-[0_12px_40px_-24px_rgba(15,42,74,0.4)] transition-shadow duration-300 hover:shadow-[0_26px_60px_-28px_rgba(15,42,74,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E4545F]"
              >
                <div className="relative aspect-[16/10] w-full overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b2038]/90 via-[#0b2038]/35 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <h3 className="text-xl font-semibold leading-tight text-white">
                      {s.name}
                    </h3>
                    <p className="mt-1.5 text-xs font-medium uppercase tracking-[0.14em] text-white/70">
                      {s.tag}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#F6A9AE] transition-colors group-hover:text-white">
                      Explore
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>

        {/* Other sectors we serve — compact supporting cards */}
        <Reveal className="mt-14 mb-10 max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#B8862F]">
            Other Sectors We Serve
          </span>
          <p className="mt-3 text-base font-light leading-relaxed text-slate-600">
            Beyond our six core sectors, Continex also works in adjacent and
            emerging sectors where international technology, manufacturing,
            investment and market-development opportunities align with our
            networks and capabilities.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {otherSectors.map((s, i) => (
            <Reveal key={s.id} delay={(i % 4) * 80}>
              <article
                data-testid={`other-sector-${s.id}`}
                className="group h-full overflow-hidden rounded-xl border border-slate-200/80 bg-white transition-shadow duration-300 hover:shadow-[0_18px_44px_-26px_rgba(15,42,74,0.45)]"
              >
                <div className="relative h-32 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#0b2038]/25" />
                </div>
                <div className="p-5">
                  <h3 className="text-sm font-semibold leading-snug text-[#0F2A4A]">
                    {s.name}
                  </h3>
                  <p className="mt-1 text-[11px] font-medium uppercase tracking-[0.12em] text-[#B8862F]">
                    {s.tag}
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-slate-600">
                    {s.text}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {/* Final open-sector CTA */}
        <Reveal className="mt-12">
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl bg-[#0F2A4A] px-8 py-10 md:flex-row md:items-center md:px-12">
            <div className="max-w-2xl">
              <h3 className="text-xl font-semibold text-white md:text-2xl">
                Looking for a sector not listed here?
              </h3>
              <p className="mt-2 text-sm font-light leading-relaxed text-white/70">
                Continex also develops opportunity-led assignments in sectors
                where our market, business and institutional networks can add
                value.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="key-sectors-cta"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#E4545F] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#c9434e]"
            >
              Discuss your market opportunity
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </div>

      {/* Click-through sector narrative modal */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="max-w-3xl gap-0 overflow-hidden p-0 max-h-[90vh]"
          data-testid="sector-dialog"
        >
          {active && (
            <div className="flex max-h-[90vh] flex-col">
              <div className="relative h-44 shrink-0 overflow-hidden md:h-52">
                <img
                  src={active.image}
                  alt={active.name}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b2038]/95 via-[#0b2038]/45 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-white/70">
                    {active.tag}
                  </p>
                  <DialogTitle className="mt-1 text-2xl font-semibold text-white md:text-3xl">
                    {active.name}
                  </DialogTitle>
                </div>
              </div>

              <div className="overflow-y-auto p-6 md:p-8">
                <DialogDescription className="text-base font-light italic leading-relaxed text-[#0F2A4A]">
                  {active.blurb}
                </DialogDescription>

                <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                  <NarrativeBlock label="India Opportunity">
                    {active.indiaOpportunity}
                  </NarrativeBlock>
                  <NarrativeBlock label="International Corridors">
                    {active.corridors}
                  </NarrativeBlock>
                  <NarrativeBlock label="How Continex Helps">
                    {active.howWeHelp}
                  </NarrativeBlock>
                  <div>
                    <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E4545F]">
                      Relevant Experience
                    </h4>
                    <ul className="mt-2 space-y-2">
                      {active.experience.map((e) => (
                        <li key={e} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#B8862F]" />
                          <span>{e}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-8 border-t border-slate-200 pt-6">
                  <Link
                    to="/contact"
                    onClick={() => setActive(null)}
                    data-testid="sector-dialog-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-[#0F2A4A] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#173a63]"
                  >
                    {active.cta}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default KeySectors;
